import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { Delete } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PinKeypadProps {
  onNumberClick: (num: string) => void;
  onDelete: () => void;
  onEnter?: () => void;
  className?: string;
  variant?: 'blue' | 'amber';
  compact?: boolean;
}

export const PinKeypad: React.FC<PinKeypadProps> = ({
  onNumberClick,
  onDelete,
  onEnter,
  className,
  variant = 'blue',
  compact = false
}) => {
  const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', ''];
  
  const hoverColors = variant === 'amber' 
    ? 'hover:bg-amber-500/20 active:bg-amber-500/30' 
    : 'hover:bg-blue-500/20 active:bg-blue-500/30';

  const handleDelete = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete();
  }, [onDelete]);

  const handleNumberClick = useCallback((num: string) => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    onNumberClick(num);
  }, [onNumberClick]);

  const buttonSize = compact 
    ? "w-14 h-12 sm:w-16 sm:h-14 text-xl sm:text-2xl rounded-xl" 
    : "w-16 h-14 sm:w-20 sm:h-16 text-xl sm:text-2xl rounded-xl sm:rounded-2xl";

  const containerWidth = compact ? "max-w-[200px] sm:max-w-[220px]" : "max-w-[220px] sm:max-w-[280px]";
  const gapSize = compact ? "gap-2" : "gap-2 sm:gap-3";

  return (
    <div className={cn(`grid grid-cols-3 ${gapSize} ${containerWidth} mx-auto`, className)}>
      {numbers.map((num, idx) => {
        if (num === '') {
          if (idx === 9) {
            // Empty space bottom-left
            return <div key={`empty-${idx}`} className={buttonSize} />;
          }
          if (idx === 11) {
            // Delete button bottom-right
            return (
              <motion.button
                key="delete"
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                onTouchEnd={handleDelete}
                className={cn(
                  buttonSize,
                  "bg-red-500/15 hover:bg-red-500/25 active:bg-red-500/35 border border-red-500/30 flex items-center justify-center text-red-400 transition-colors"
                )}
                style={{ touchAction: 'manipulation' }}
              >
                <Delete className={compact ? "w-5 h-5" : "w-5 h-5 sm:w-6 sm:h-6"} />
              </motion.button>
            );
          }
        }
        
        return (
          <motion.button
            key={`num-${idx}`}
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNumberClick(num)}
            className={cn(
              buttonSize,
              "bg-zinc-800/60 border border-zinc-700/50 flex items-center justify-center font-semibold text-white transition-all duration-150",
              hoverColors
            )}
            style={{ touchAction: 'manipulation' }}
          >
            {num}
          </motion.button>
        );
      })}
    </div>
  );
};
